import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";

/**
 * Loads only the recommendation area, preserving the main product form and gallery.
 * @typedef {object} CompleteSetRefs
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 * @extends Component<CompleteSetRefs>
 */
class BcsCompleteSetRecommendations extends Component {
  #controller;
  #loading = false;
  #motion = window.matchMedia("(prefers-reduced-motion: reduce)");

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.addEventListener("scroll", this.syncControls, {
      capture: true,
      passive: true,
      signal: this.#controller.signal,
    });
    this.#syncControls();
    this.#load();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#loading = false;
  }

  /** @param {Event} _event */
  previous(_event) {
    this.#move(-1);
  }

  /** @param {Event} _event */
  next(_event) {
    this.#move(1);
  }

  /** @param {Event} _event */
  syncControls(_event) {
    this.#syncControls();
  }

  #move(direction) {
    const track = this.refs.track;
    if (!track) return;
    const cards = [...track.children];
    const current = cards.reduce((closest, card, index) => {
      const distance = Math.abs(
        card.getBoundingClientRect().left - track.getBoundingClientRect().left,
      );
      const closestDistance = Math.abs(
        cards[closest].getBoundingClientRect().left -
          track.getBoundingClientRect().left,
      );
      return distance < closestDistance ? index : closest;
    }, 0);
    cards[
      Math.max(0, Math.min(cards.length - 1, current + direction))
    ]?.scrollIntoView({
      behavior: this.#motion.matches ? "auto" : "smooth",
      block: "nearest",
      inline: "start",
    });
  }

  #syncControls() {
    const { track, previousButton, nextButton } = this.refs;
    if (!track || !previousButton || !nextButton) return;
    const position = Math.abs(track.scrollLeft);
    previousButton.disabled = position <= 1;
    nextButton.disabled = position >= track.scrollWidth - track.clientWidth - 1;
  }

  async #load() {
    if (this.dataset.recommendationsLoaded === "true" || this.#loading) return;
    if (!this.dataset.url || !this.dataset.sectionId) return;
    this.#loading = true;
    const signal = this.#controller.signal;
    const urls = [this.dataset.url, this.dataset.fallbackUrl].filter(Boolean);

    try {
      for (const path of urls) {
        const url = new URL(path, window.location.origin);
        const response = await fetch(url, { signal, cache: "no-store" });
        if (!response.ok) continue;
        const html = await response.text();
        const document = new DOMParser().parseFromString(html, "text/html");
        const incoming = [
          ...document.querySelectorAll("bcs-complete-set-recommendations"),
        ].find(
          (element) =>
            element.dataset.hydrationKey === this.dataset.hydrationKey,
        );
        if (!incoming?.querySelector("[data-bcs-complete-set]")) continue;
        if (signal.aborted || !this.isConnected) return;
        await morphSection(this.dataset.sectionId, html, {
          mode: "hydration",
          injectStylesheet: true,
        });
        if (signal.aborted || !this.isConnected) return;
        this.dataset.recommendationsLoaded = "true";
        this.hidden = false;
        this.updatedCallback();
        return;
      }
      this.hidden = true;
    } catch (error) {
      if (error.name !== "AbortError") {
        this.hidden = true;
        console.warn(
          "[bcs-complete-set] Could not load recommendations.",
          error,
        );
      }
    } finally {
      if (!signal.aborted) this.#loading = false;
    }
  }
}

if (!customElements.get("bcs-complete-set-recommendations")) {
  customElements.define(
    "bcs-complete-set-recommendations",
    BcsCompleteSetRecommendations,
  );
}
